import { capitalize } from 'lodash';

import { VALUE_CONCLUSION_TYPES } from 'components/underwriting/constants';
import { ADJUSTMENT_TYPES } from './constants';
import { RentRollUnit } from 'types/underwriting/income-approach';
import { RentLossItem } from 'types/underwriting/income-approach/cap-rate-conclusion';
import { ConclusionType, FinalValues, RentLossType } from './types';
import { formatCurrencyFloat, formatCurrencyInt } from 'shared/utils/numberFormatters';

export const adjustmentTypeOptionsForValueType = (valueConclusionTypes: string[]) => {
  return [
    ...(valueConclusionTypes.includes(VALUE_CONCLUSION_TYPES.AS_IS)
      ? [
          {
            label: ADJUSTMENT_TYPES.NPV,
            value: ADJUSTMENT_TYPES.NPV,
          },
        ]
      : []),
    ...(valueConclusionTypes.includes(VALUE_CONCLUSION_TYPES.AS_STABILIZED)
      ? [
          {
            label: ADJUSTMENT_TYPES.AS_STABILIZED,
            value: ADJUSTMENT_TYPES.AS_STABILIZED,
          },
        ]
      : []),
    ...(valueConclusionTypes.includes(VALUE_CONCLUSION_TYPES.AS_COMPLETE)
      ? [
          {
            label: ADJUSTMENT_TYPES.AS_COMPLETE,
            value: ADJUSTMENT_TYPES.AS_COMPLETE,
          },
        ]
      : []),
  ];
};

export const getRentLossPath = (type: RentLossType, conclusionType: ConclusionType) => {
  return `incomeApproach.capRateConclusion.${conclusionType}${capitalize(type)}RentLossItems`;
};

export const getRentLossRowTitle = (
  rentLossItem: RentLossItem,
  units: RentRollUnit[],
  rentLossHeaderPrefix: string
) => {
  const unitNamesInOrder = (units || []).flatMap((unit, index) => {
    if (rentLossItem.units?.includes(unit?.id)) {
      return unit?.number || index + 1;
    }
    return [];
  });

  const maxUnitsToName = 4;
  if (unitNamesInOrder.length > maxUnitsToName) {
    return rentLossHeaderPrefix;
  }

  // definitely a bug here with unitNamesInOrder.join
  let title = `${rentLossHeaderPrefix} (Unit${unitNamesInOrder.length > 1 ? 's' : ''}`;
  title = `${title} ${unitNamesInOrder.join(', ')})`;

  return title;
};

export const DEFAULT_FINAL_VALUES = {
  indicatedValue: {
    total: 0,
    rounded: 0,
    perUnit: 0,
    psf: 0,
  },
  finalValueAsStabilized: {
    total: 0,
    rounded: 0,
    perUnit: 0,
    psf: 0,
  },
  finalValueAsComplete: {
    total: 0,
    rounded: 0,
    perUnit: 0,
    psf: 0,
  },
  finalValueAsIs: {
    total: 0,
    rounded: 0,
    perUnit: 0,
    psf: 0,
  },
  asCompleteMarketValueWithDiscount: 0,
  asStabilizedMarketValueWithDiscount: 0,
};

export const mapValuesToDisplay = (finalValues: FinalValues) => {
  const {
    indicatedValue,
    finalValueAsComplete,
    finalValueAsStabilized,
    finalValueAsIs,
    asCompleteMarketValueWithDiscount,
    asStabilizedMarketValueWithDiscount,
  } = finalValues;
  return {
    capitalizationConclusion: formatCurrencyInt(indicatedValue.total),
    capitalizationConclusionRounded: formatCurrencyInt(indicatedValue.rounded),
    finalValueAsStabilized: formatCurrencyInt(finalValueAsStabilized.total),
    finalValueAsStabilizedRoundedUnformatted: finalValueAsStabilized.rounded,
    finalValueAsStabilizedRounded: formatCurrencyInt(finalValueAsStabilized.rounded),
    finalValueAsStabilizedPerUnit: formatCurrencyInt(finalValueAsStabilized.perUnit),
    finalValueAsStabilizedPerSf: formatCurrencyFloat(finalValueAsStabilized.psf),
    finalValueAsComplete: formatCurrencyInt(finalValueAsComplete.total),
    finalValueAsCompleteRoundedUnformatted: finalValueAsComplete.rounded,
    finalValueAsCompleteRounded: formatCurrencyInt(finalValueAsComplete.rounded),
    finalValueAsCompletePerUnit: formatCurrencyInt(finalValueAsComplete.perUnit),
    finalValueAsCompletePerSf: formatCurrencyFloat(finalValueAsComplete.psf),
    finalValueAsIs: formatCurrencyInt(finalValueAsIs.total),
    finalValueAsIsRoundedUnformatted: finalValueAsIs.rounded,
    finalValueAsIsRounded: formatCurrencyInt(finalValueAsIs.rounded),
    finalValueAsIsPerUnit: formatCurrencyInt(finalValueAsIs.perUnit),
    finalValueAsIsPerSf: formatCurrencyFloat(finalValueAsIs.psf),
    asCompleteMarketValueWithDiscount: formatCurrencyInt(asCompleteMarketValueWithDiscount),
    asStabilizedMarketValueWithDiscount: formatCurrencyInt(asStabilizedMarketValueWithDiscount),
  };
};
